import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../shared';
import { EventsSchedule } from '../EventsSchedule';

const STORAGE_KEY = 'wiki_panel_last';

// Mark enchant bonuses data
// Stats are random at +5, +7, +9. At +10 it's a single fixed stat.
// Tier 1 (+5) doubles at +6
// Tier 2 (+7) doubles at +8
// Tier 3 (+9) - no doubling
// +10 - single fixed stat
// Row 1: Avadon, Doom, Power
// Row 2: Speed, Hunter, Defender
// Row 3: Divine, Immunity, Blood
const MARKS = [
  {
    id: 'avadon',
    icon: '/icons/items/mark_avadon.png',
    color: '#6b5b95',
    baseStat: {
      name: 'Casting Spd',
      tiers: ['+5%', '+8%', '+10%', '+12%', '+15%'],
    },
    tier1: [
      { name: 'M.Atk', value: 50 },
      { name: 'Max HP', value: 50 },
      { name: 'Max CP', value: 40 },
    ],
    tier2: [
      { name: 'Magic Critical Power', value: '2%' },
      { name: 'Elemental Magic Critical Power', value: '2.5%' },
      { name: 'Dark/Holy Magic Critical Power', value: '2%' },
      { name: 'Heal Other Effect', value: '5%' },
    ],
    tier3: [
      { name: 'Magic Crit Rate', value: '1.5%' },
      { name: 'Casting Spd', value: '2%' },
      { name: 'mReuse', value: '-8%' },
    ],
    tier4: { name: 'WIT', value: 1 },
  },
  {
    id: 'doom',
    icon: '/icons/items/mark_doom.png',
    color: '#8b0000',
    baseStat: {
      name: 'Atk.Spd',
      tiers: ['5%', '8%', '10%', '12%', '15%'],
    },
    tier1: [
      { name: 'P.Atk', value: 35 },
      { name: 'Max HP', value: 70 },
      { name: 'Max CP', value: 50 },
    ],
    tier2: [
      { name: 'P.Atk with bow', value: '2.5%' },
      { name: 'Critical', value: 15 },
      { name: 'P.Skill Power', value: '2.5%' },
    ],
    tier3: [
      { name: 'Atk.Spd', value: '2%' },
      { name: 'Critical Power', value: '2.5%' },
      { name: 'pReuse', value: '-4%' },
    ],
    tier4: { name: 'DEX', value: 1 },
  },
  {
    id: 'power',
    icon: '/icons/items/mark_power.png',
    color: '#e74c3c',
    baseStat: {
      name: 'P.Atk',
      tiers: ['5%', '8%', '10%', '12%', '15%'],
    },
    // Power has identical enchant bonuses with Doom
    tier1: [
      { name: 'P.Atk', value: 35 },
      { name: 'Max HP', value: 70 },
      { name: 'Max CP', value: 50 },
    ],
    tier2: [
      { name: 'P.Atk with bow', value: '2.5%' },
      { name: 'Critical', value: 15 },
      { name: 'P.Skill Power', value: '2.5%' },
    ],
    tier3: [
      { name: 'Atk.Spd', value: '2%' },
      { name: 'Critical Power', value: '2.5%' },
      { name: 'pReuse', value: '-4%' },
    ],
    tier4: { name: 'DEX', value: 1 },
  },
  {
    id: 'speed',
    icon: '/icons/items/mark_speed.png',
    color: '#88d8b0',
    baseStat: {
      name: 'Speed',
      tiers: ['+6', '+9', '+11', '+14', '+16'],
    },
    tier1: { name: 'Max HP, Max CP', value: '70, 50', grouped: true },
    tier2: { name: 'P.Def, M.Def', value: '3%', grouped: true },
    tier3: { name: 'pReuse, mReuse, Run Speed', value: '-8%, -8%, +5', grouped: true },
    tier4: { name: 'CON', value: 2 },
  },
  {
    id: 'hunter',
    icon: '/icons/items/mark_hunter.png',
    color: '#ff6f69',
    baseStat: {
      name: 'Critical',
      tiers: ['+50', '+80', '+100', '+120', '+150'],
    },
    // Hunter has identical enchant bonuses with Doom
    tier1: [
      { name: 'P.Atk', value: 35 },
      { name: 'Max HP', value: 70 },
      { name: 'Max CP', value: 50 },
    ],
    tier2: [
      { name: 'P.Atk with bow', value: '2.5%' },
      { name: 'Critical', value: 15 },
      { name: 'P.Skill Power', value: '2.5%' },
    ],
    tier3: [
      { name: 'Atk.Spd', value: '2%' },
      { name: 'Critical Power', value: '2.5%' },
      { name: 'pReuse', value: '-4%' },
    ],
    tier4: { name: 'DEX', value: 1 },
  },
  {
    id: 'defender',
    icon: '/icons/items/mark_defender.png',
    color: '#4a90d9',
    baseStat: {
      name: 'P.Def',
      tiers: ['7%', '10%', '12%', '15%', '18%'],
    },
    tier1: { name: 'Max HP, Max CP', value: '70, 50', grouped: true },
    tier2: { name: 'P.Def, M.Def', value: '3%', grouped: true },
    tier3: { name: 'pReuse, mReuse, Run Speed', value: '-8%, -8%, +5', grouped: true },
    tier4: { name: 'CON', value: 2 },
  },
  {
    id: 'divine',
    icon: '/icons/items/mark_divine.png',
    color: '#ffd700',
    baseStat: {
      name: 'M.Atk',
      tiers: ['5%', '8%', '10%', '12%', '15%'],
    },
    tier1: [
      { name: 'M.Atk', value: 50 },
      { name: 'Max HP', value: 50 },
      { name: 'Max CP', value: 40 },
    ],
    tier2: [
      { name: 'Magic Critical Power', value: '2%' },
      { name: 'Elemental Magic Critical Power', value: '2.5%' },
      { name: 'Dark/Holy Magic Critical Power', value: '2%' },
      { name: 'Heal Other Effect', value: '5%' },
    ],
    tier3: [
      { name: 'Magic Crit Rate', value: '1.5%' },
      { name: 'Casting Spd', value: '2%' },
      { name: 'mReuse', value: '-8%' },
    ],
    tier4: { name: 'WIT', value: 1 },
  },
  {
    id: 'immunity',
    icon: '/icons/items/mark_immunity.png',
    color: '#9b59b6',
    baseStat: {
      name: 'M.Def',
      tiers: ['7%', '10%', '12%', '15%', '18%'],
    },
    tier1: { name: 'Max HP, Max CP', value: '70, 50', grouped: true },
    tier2: { name: 'P.Def, M.Def', value: '3%', grouped: true },
    tier3: { name: 'pReuse, mReuse, Run Speed', value: '-8%, -8%, +5', grouped: true },
    tier4: { name: 'CON', value: 2 },
  },
  {
    id: 'blood',
    icon: '/icons/items/mark_blood.png',
    color: '#dc143c',
    baseStat: {
      name: 'Maximum HP',
      tiers: ['+7%', '+10%', '+12%', '+15%', '+18%'],
    },
    tier1: { name: 'Max HP, Max CP', value: '70, 50', grouped: true },
    tier2: { name: 'P.Def, M.Def', value: '3%', grouped: true },
    tier3: { name: 'pReuse, mReuse, Run Speed', value: '-8%, -8%, +5', grouped: true },
    tier4: { name: 'CON', value: 2 },
  },
];

// Wiki sections for navigation
const WIKI_SECTIONS = [
  { id: 'events', icon: '📅', image: '/icons/events.svg' },
  { id: 'marks', icon: '🏅', image: '/icons/marks.svg' },
];

// Icon component using CSS mask for SVG color control (same as menu icons)
function WikiNavIcon({ section }) {
  if (!section.image) {
    return <span className="wiki-nav-icon">{section.icon}</span>;
  }

  return (
    <span
      className="wiki-nav-icon wiki-nav-icon-svg"
      style={{
        maskImage: `url(${section.image})`,
        WebkitMaskImage: `url(${section.image})`,
      }}
    />
  );
}

// Format stat value for display
const formatValue = (value) => {
  if (typeof value === 'string') return value.startsWith('-') ? value : `+${value}`;
  return `+${value}`;
};

// Check if tier has grouped stats (all stats given together)
const isGrouped = (tier) => {
  if (!tier) return false;
  if (tier.grouped === true) return true;
  // Single stat object = grouped (you get that one stat)
  if (!Array.isArray(tier) && tier.name && tier.value) return true;
  return false;
};

// Get stats array from tier (handles all formats)
const getTierStats = (tier) => {
  if (!tier) return [];
  if (Array.isArray(tier)) return tier;
  if (tier.stats) return tier.stats;
  // Single stat object - check for comma-separated names/values
  if (tier.name && tier.value !== undefined) {
    const names = tier.name.split(',').map(s => s.trim());
    const values = String(tier.value).split(',').map(s => s.trim());
    // If multiple names, expand into individual stats
    if (names.length > 1) {
      return names.map((name, idx) => ({
        name,
        // Use corresponding value or repeat the single value
        value: values[idx] || values[0]
      }));
    }
    return [tier];
  }
  return [];
};

// Double a stat value
const doubleValue = (value) => {
  if (typeof value === 'string') {
    // Handle percentage strings like "2.5%" or "-4%"
    const match = value.match(/^(-?)(\d+(?:\.\d+)?)(%?)$/);
    if (match) {
      const sign = match[1];
      const num = parseFloat(match[2]) * 2;
      const suffix = match[3];
      return `${sign}${num}${suffix}`;
    }
    return value;
  }
  return value * 2;
};

export function WikiPanel({ active }) {
  const { t } = useLanguage();

  const [activeSection, setActiveSection] = useState('events');
  const [expandedMark, setExpandedMark] = useState(null);
  const detailPanelRef = useRef(null);

  // Scroll to detail panel when mark is expanded
  useEffect(() => {
    if (expandedMark && detailPanelRef.current) {
      setTimeout(() => {
        detailPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [expandedMark]);

  // Restore state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.activeSection) setActiveSection(state.activeSection);
      }
    } catch (e) {}
  }, []);

  // Save state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        activeSection
      }));
    } catch (e) {}
  }, [activeSection]);

  const toggleMark = (markId) => {
    setExpandedMark(expandedMark === markId ? null : markId);
  };

  const renderMarkBonuses = () => {
    const selectedMark = expandedMark ? MARKS.find(m => m.id === expandedMark) : null;
    const selectedIndex = expandedMark ? MARKS.findIndex(m => m.id === expandedMark) : -1;
    const selectedRow = selectedIndex >= 0 ? Math.floor(selectedIndex / 3) : -1;

    // Group marks into rows of 3
    const rows = [];
    for (let i = 0; i < MARKS.length; i += 3) {
      rows.push(MARKS.slice(i, i + 3));
    }

    const renderDetailPanel = () => (
      <div ref={detailPanelRef} className="mark-detail-panel" style={{ '--mark-color': selectedMark.color }}>
        <div className="mark-detail-header">
          <img className="mark-icon" src={selectedMark.icon} alt="" />
          <span className="mark-name">{t(`wikiMark_${selectedMark.id}`)}</span>
          <span className="mark-close" onClick={() => setExpandedMark(null)}>×</span>
        </div>

        <div className="mark-card-content">
          {/* Base Mark Tiers */}
          <div className="base-stat-block">
            <div className="base-stat-label">{selectedMark.baseStat.name}</div>
            <div className="base-stat-tiers">
              {['', 'M1', 'M2', 'M3', 'M4'].map((tier, idx) => (
                <div key={idx} className="base-stat-tier">
                  <span className="base-tier-name">{tier || t('wikiPlain')}</span>
                  <span className="base-tier-value">{selectedMark.baseStat.tiers[idx]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Enchant Bonuses Header */}
          <div className="enchant-section-header">{t('wikiEnchantBonuses')}</div>

          {/* Tier 1: +5/+6/+8 */}
          <div className="tier-block tier-1">
            <div className="tier-header">
              <span className="tier-levels">+5 / +6 / +8</span>
              {isGrouped(selectedMark.tier1) && <span className="tier-grouped-label">{t('wikiAllStats')}</span>}
              {!isGrouped(selectedMark.tier1) && <span className="tier-random-label">{t('wikiRandomStat')}</span>}
            </div>
            <div className={`mark-stats-table tier1-table ${isGrouped(selectedMark.tier1) ? 'grouped' : ''}`}>
              <div className="stats-header">
                <span className="stat-name-col">{t('wikiStatName')}</span>
                <span className="stat-level-col">+5</span>
                <span className="stat-level-col">+6</span>
                <span className="stat-level-col">+8</span>
              </div>
              {getTierStats(selectedMark.tier1).map((stat, idx) => (
                <div key={idx} className="stats-row">
                  <span className="stat-name-col">{stat.name}</span>
                  <span className="stat-level-col">{formatValue(stat.value)}</span>
                  <span className="stat-level-col">{formatValue(doubleValue(stat.value))}</span>
                  <span className="stat-level-col highlight-max">{formatValue(doubleValue(doubleValue(stat.value)))}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tier 2: +7/+8 */}
          <div className="tier-block">
            <div className="tier-header">
              <span className="tier-levels">+7 / +8</span>
              {isGrouped(selectedMark.tier2) && <span className="tier-grouped-label">{t('wikiAllStats')}</span>}
              {!isGrouped(selectedMark.tier2) && <span className="tier-random-label">{t('wikiRandomStat')}</span>}
            </div>
            <div className={`mark-stats-table ${isGrouped(selectedMark.tier2) ? 'grouped' : ''}`}>
              <div className="stats-header">
                <span className="stat-name-col">{t('wikiStatName')}</span>
                <span className="stat-level-col">+7</span>
                <span className="stat-level-col">+8</span>
              </div>
              {getTierStats(selectedMark.tier2).map((stat, idx) => (
                <div key={idx} className="stats-row">
                  <span className="stat-name-col">{stat.name}</span>
                  <span className="stat-level-col">{formatValue(stat.value)}</span>
                  <span className="stat-level-col highlight">{formatValue(doubleValue(stat.value))}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tier 3: +9 */}
          <div className="tier-block">
            <div className="tier-header">
              <span className="tier-levels">+9</span>
              {isGrouped(selectedMark.tier3) && <span className="tier-grouped-label">{t('wikiAllStats')}</span>}
              {!isGrouped(selectedMark.tier3) && <span className="tier-random-label">{t('wikiRandomStat')}</span>}
            </div>
            <div className={`mark-stats-table tier3-table ${isGrouped(selectedMark.tier3) ? 'grouped' : ''}`}>
              <div className="stats-header">
                <span className="stat-name-col">{t('wikiStatName')}</span>
                <span className="stat-level-col">+9</span>
              </div>
              {getTierStats(selectedMark.tier3).map((stat, idx) => (
                <div key={idx} className="stats-row">
                  <span className="stat-name-col">{stat.name}</span>
                  <span className="stat-level-col">{formatValue(stat.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tier 4: +10 (fixed) */}
          <div className="tier-block tier-fixed">
            <div className="tier-header">
              <span className="tier-levels">+10</span>
            </div>
            <div className="fixed-stat-row">
              <span className="stat-name">{selectedMark.tier4.name}</span>
              <span className="stat-value highlight-max">{formatValue(selectedMark.tier4.value)}</span>
            </div>
          </div>

          <p className="mark-note">{t('wikiMarkNote')}</p>
        </div>
      </div>
    );

    return (
      <div className="marks-container">
        <p className="wiki-intro">{t('wikiMarksIntro')}</p>

        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="marks-row">
            <div className="marks-grid">
              {row.map(mark => (
                <div
                  key={mark.id}
                  className={`mark-card ${expandedMark === mark.id ? 'selected' : ''}`}
                  style={{ '--mark-color': mark.color }}
                  onClick={() => toggleMark(mark.id)}
                >
                  <div className="mark-card-header">
                    <img className="mark-icon" src={mark.icon} alt="" />
                    <span className="mark-name">{t(`wikiMark_${mark.id}`)}</span>
                  </div>
                </div>
              ))}
            </div>
            {selectedMark && rowIndex === selectedRow && renderDetailPanel()}
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'events':
        return <EventsSchedule />;
      case 'marks':
        return renderMarkBonuses();
      default:
        return <p className="wiki-placeholder">{t('wikiComingSoon')}</p>;
    }
  };

  if (!active) return null;

  return (
    <div className="calculator-panel active wiki-panel">
      {/* Section Navigation */}
      <div className="wiki-nav">
        {WIKI_SECTIONS.map(section => (
          <button
            key={section.id}
            className={`wiki-nav-btn ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            <WikiNavIcon section={section} />
            <span className="wiki-nav-label">{t(`wikiSection_${section.id}`)}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <Card title={t(`wikiSection_${activeSection}`)}>
        {renderContent()}
      </Card>
    </div>
  );
}
